<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue"

import { KirbyBlock } from "../../queries/blocks"
import VideoPlayer from "../Media/VideoPlayer.vue"
import { useRouter } from "vue-router"

const fetchOembedRatio = async (url: string): Promise<number> => {
  try {
    const oembedUrl = url.includes("vimeo")
      ? `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}`
      : `https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`
    const res = await fetch(oembedUrl)
    if (res.ok) {
      const data = await res.json()
      if (data.width && data.height) {
        return data.height / data.width
      }
    }
  } catch {}
  return 9 / 16
}

const props = withDefaults(
  defineProps<{
    data: KirbyBlock<"video">["content"]
    isVisible: boolean
    isActive?: boolean
    autoplay?: boolean
  }>(),
  { isActive: false, autoplay: false },
)

const emit = defineEmits<{
  heightChange: [height: number]
}>()

const ratio = ref(9 / 16)
const thumbnailError = ref(false)
const width = ref<string>("100%")
const wrapper = ref<HTMLElement>()
const container = ref<HTMLElement>()

const thumbnailUrl = computed(() => {
  const url = props.data.url
  const watchMatch = url.match(/youtube\.com\/watch\?v=([^&]+)/)
  const shortMatch = url.match(/youtu\.be\/([^?]+)/)
  const embedMatch = url.match(/youtube\.com\/embed\/([^?]+)/)
  const id = (watchMatch ?? shortMatch ?? embedMatch)?.[1] ?? null
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null
})

const onResize = () => {
  if (!wrapper.value) return
  const parentHeight = container.value?.clientHeight ?? window.innerHeight - 64
  if (parentHeight <= 0) return
  const maxWidth = container.value?.clientWidth ?? Infinity
  const renderedWidth = Math.min(parentHeight / ratio.value, maxWidth)
  width.value = renderedWidth + "px"

  // Report the rendered player height so the gallery can hug it with the caption.
  const renderedHeight = renderedWidth * ratio.value
  if (props.isActive && renderedHeight > 0) {
    emit("heightChange", renderedHeight)
  }
}

onMounted(async () => {
  window.addEventListener("resize", onResize)
  ratio.value = await fetchOembedRatio(props.data.url)
  onResize()
})

onBeforeUnmount(() => {
  window.removeEventListener("resize", onResize)
})

watch(() => props.isVisible, onResize)

watch(
  () => props.isActive,
  async value => {
    if (value) {
      await nextTick()
      onResize()
    }
  },
)

const onGetRatio = (videoRatio: number) => {
  ratio.value = videoRatio
  onResize()
}

const router = useRouter()
const route = router.currentRoute.value
const onClickExit = () => {
  const newQuery = Object.fromEntries(
    Object.entries(route.query).filter(([key]) => key !== "i"),
  )
  router
    .replace({ path: route.path, query: newQuery, hash: undefined })
    .catch(err => console.error("Navigation error:", err))
}
</script>

<template>
  <div
    ref="container"
    data-slide-content="video"
    class="flex items-center justify-center h-full relative"
    :class="{ 'md:pointer-events-none': !isActive }"
    @click.self="onClickExit"
  >
    <div ref="wrapper" class="mx-auto" :style="{ width }">
      <VideoPlayer
        v-if="isVisible"
        @get-ratio="onGetRatio"
        :src="data.url"
        :providerName="data.url.includes('vimeo') ? 'vimeo' : 'youtube'"
        :actualRatio="true"
        :autoplay="autoplay"
      />
      <div v-else class="relative" :style="{ aspectRatio: 1 / ratio }">
        <img
          v-if="thumbnailUrl && !thumbnailError"
          :src="thumbnailUrl"
          class="object-cover w-full h-full"
          @error="thumbnailError = true"
        />
        <div
          v-else
          class="bg-black flex items-center justify-center w-full h-full"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            class="w-[20%] max-w-16"
            fill="white"
          >
            <polygon points="5,3 19,12 5,21" />
          </svg>
        </div>
      </div>
    </div>
  </div>
</template>
